import Util from './util'

// Represents a polygon or polyline edge
class Edge {
  _inNormal: [number, number]
  _outNormal: [number, number]

  constructor(
    public current: [number, number],
    public next: [number, number]
  ) {
    this._inNormal = this.inwardsNormal()
    this._outNormal = this.outwardsNormal()
  }

  outwardsNormal(): [number, number] {
    const inwards = this.inwardsNormal()
    return [-inwards[0], -inwards[1]]
  }

  inwardsNormal(): [number, number] {
    const dx = this.next[0] - this.current[0]
    const dy = this.next[1] - this.current[1]
    const edgeLength = Math.sqrt(dx * dx + dy * dy)

    if (edgeLength === 0) {
      throw new Error('Vertices overlap')
    }

    return [-dy / edgeLength, dx / edgeLength]
  }

  /**
   * Offsets the edge by dx, dy
   */
  offset(dx: number, dy: number) {
    return Edge.offsetEdge(this.current, this.next, dx, dy)
  }

  inverseOffset(dx: number, dy: number) {
    return Edge.offsetEdge(this.next, this.current, dx, dy)
  }

  static offsetEdge(current: [number, number], next: [number, number], dx: number, dy: number) {
    return new Edge([current[0] + dx, current[1] + dy], [next[0] + dx, next[1] + dy])
  }

  inverse() {
    return new Edge(this.next, this.current)
  }
}

class Offset {
  static offset(vertices: [number, number][], thicknesses: number[]) {
    const edges = Offset._buildEdges(vertices)

    const offsetData = Offset.offsetSegmentList(edges, thicknesses)

    let insetPoints: [number, number][] | undefined
    let outsetPoints: [number, number][]

    let insetEdges: [[number, number], [number, number]][]
    let outsetEdges: [[number, number], [number, number]][]

    let pureOutsetPoints

    if (Offset.isPolygon(vertices)) {
      const ccPadding: [number, number][] = Util.getCounterClockwisePoints(offsetData.padding)
      const ccMargin: [number, number][] = Util.getCounterClockwisePoints(offsetData.margin)

      insetPoints = Offset.clipOffsetEdges(ccPadding, true)
      outsetPoints = Offset.clipOffsetEdges(ccMargin, true)

      pureOutsetPoints = outsetPoints

      insetEdges = Offset.pointsToEdges(insetPoints)
      outsetEdges = Offset.pointsToEdges(outsetPoints)
    } else {
      const padding = Offset.clipOffsetEdges(offsetData.padding, false)
      const margin = Offset.clipOffsetEdges(offsetData.margin, false)

      insetEdges = Offset.pointsToEdges(padding)
      outsetEdges = Offset.pointsToEdges(margin)

      pureOutsetPoints = margin

      outsetPoints = Offset.getClosedOffsetPoints(margin, padding)
    }

    const edgePolygons = Offset.getEdgePolygons(insetEdges, outsetEdges)

    return {
      insetPoints,
      outsetPoints,
      insetEdges,
      outsetEdges,
      edgePolygons,
      pureOutsetPoints,
    }
  }

  /*
    Returns an array of polygons (each as an array of points), one for each edge
    in the given insetEdges/outsetEdges arrays. The polygons are formed by connecting
    the inset/outset edges at either side, so that each returned polygon surrounds
    one edge completely.
  */
  static getEdgePolygons(
    insetEdges: [[number, number], [number, number]][],
    outsetEdges: [[number, number], [number, number]][]
  ) {
    if (insetEdges.length !== outsetEdges.length)
      throw new Error(
        `getEdgePolygons(...) requires the same number of inset edges as outset edges, received insetEdges: ${insetEdges.length}, outsetEdges: ${outsetEdges.length}`
      )

    const edgePolygons = []

    // Note that we are only using the unique points on the polygon rather than
    // giving an explicit list of edges.
    for (let i = 0; i < insetEdges.length; i += 1) {
      edgePolygons.push(insetEdges[i].concat(outsetEdges[i].slice().reverse()))
    }

    return edgePolygons
  }

  /*
    Given two sets of points, one for the outset points of a polyline and another for
    the inset points of the same polyline, this will return a single array of points
    which includes the original inset/outset points, but with two edges added connecting
    the inset and offset edges at either end of the polyline.

    The ordering of points in the returned polygon is:
    -insetPoints (in order given)
    -the last point of insetPoints, the last point of outsetPoints (connecting the far end of the polyline)
    -outsetPoints points in reverse order
    -the first outsetPoint (original ordering), the first insetPoint (connecting the near end of the polyline)
  */
  static getClosedOffsetPoints(outsetPoints: [number, number][], insetPoints: [number, number][]) {
    let polygonPoints = insetPoints.slice()
    polygonPoints.push(insetPoints[insetPoints.length - 1])
    polygonPoints.push(outsetPoints[outsetPoints.length - 1])
    polygonPoints = polygonPoints.concat(outsetPoints.slice().reverse())
    polygonPoints.push(outsetPoints[0])
    polygonPoints.push(insetPoints[0])

    return polygonPoints
  }

  static _buildEdges(vertices: [number, number][]) {
    const edges = []

    // This should work for both polygons and polylines since
    // we repeat the last vertex in the case of polygons.
    const edgeCount = vertices.length - 1

    for (let i = 0; i < edgeCount; i += 1) {
      edges.push(new Edge(vertices[i], vertices[i + 1]))
    }

    return edges
  }

  static isPolygon(vertices: [number, number][]) {
    return vertices.length > 2 && Offset.equals(vertices[0], vertices[vertices.length - 1])
  }

  static _offsetSegment(e1: Edge, dist: number) {
    const offsets = [
      e1.offset(e1._inNormal[0] * dist, e1._inNormal[1] * dist),
      e1.offset(e1._inNormal[0] * -dist, e1._inNormal[1] * -dist),
    ]

    const margin = [offsets[1].current, offsets[1].next]
    const padding = [offsets[0].current, offsets[0].next]

    return {
      padding,
      margin,
    }
  }

  static pointsToEdges(points: [number, number][]): [[number, number], [number, number]][] {
    const edges: [[number, number], [number, number]][] = []
    for (let i = 0; i < points.length; i += 2) {
      edges.push([points[i], points[i + 1]])
    }

    return edges
  }

  static clipOffsetEdges(edgePoints: [number, number][], isPolygon: boolean): [number, number][] {
    // No clipping necessary if there is only one edge (i.e. 2 edge points)
    if (edgePoints.length === 2) {
      return edgePoints.slice()
    }

    const clippedEdgePoints: [number, number][] = []

    // The last two points are for the edge connecting the last point back
    // to the first point, which is only present in polygons.
    const length = isPolygon ? edgePoints.length : edgePoints.length - 2

    for (let i = 0; i < length; i += 2) {
      // First edge
      const p1 = edgePoints[i]
      const p2 = edgePoints[i + 1]

      // Second edges
      const thirdPointIndex = (i + 2) % edgePoints.length
      const fourthPointIndex = (i + 3) % edgePoints.length
      const p3 = edgePoints[thirdPointIndex]
      const p4 = edgePoints[fourthPointIndex]

      const line1 = Util.lineFromPoints(p1, p2)
      const line2 = Util.lineFromPoints(p3, p4)

      const intersectionPoint_ = Util.getIntersectionPoint(line1.slope, line1.yShift, line2.slope, line2.yShift)
      const intersectionPoint: [number, number] = [intersectionPoint_.x, intersectionPoint_.y]

      // set end point of first segment to intersection point
      // set start point of second segment to intersection point
      clippedEdgePoints[i + 1] = [...intersectionPoint]
      clippedEdgePoints[thirdPointIndex] = [...intersectionPoint]

      if (clippedEdgePoints[i] === undefined) {
        clippedEdgePoints[i] = edgePoints[i]
      }
      if (clippedEdgePoints[fourthPointIndex] === undefined) {
        clippedEdgePoints[fourthPointIndex] = edgePoints[fourthPointIndex]
      }
    }

    return clippedEdgePoints
  }

  static offsetSegmentList(edges: Edge[], thicknesses: number[]) {
    let padding: [number, number][] = []
    let margin: [number, number][] = []

    for (let i = 0; i < edges.length; i += 1) {
      const segmentData = Offset._offsetSegment(edges[i], thicknesses[i] / 2)
      padding = padding.concat(segmentData.padding)
      margin = margin.concat(segmentData.margin)
    }

    return { padding, margin }
  }

  static equals(p1: [number, number], p2: [number, number]) {
    return p1[0] === p2[0] && p1[1] === p2[1]
  }
}

export default Offset
